import React, { useMemo, useState } from "react";
import {
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Inbox,
  Mail,
  Phone,
  FileDown,
  Trash2,
} from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 8;

// Files are served from the backend host (configurable via .env).
const API_HOST = process.env.REACT_APP_API_HOST || "http://127.0.0.1:8000";

// Resolve a stored CV path (e.g. "uploads/cvs/x.pdf") to a full URL.
const cvUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_HOST.replace(/\/$/, "")}/${String(path).replace(/^\/?/, "")}`;
};

// Show a date like "12 Mar 2025, 14:30".
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// is_active may arrive as true, 1 or "1".
const isActiveValue = (r) =>
  r.is_active === true || r.is_active === 1 || r.is_active === "1";

// A response is soft-deleted when the API includes a deleted_at timestamp.
const isDeleted = (r) => !!r.deleted_at;

const JobResponsesTable = ({
  responses = [],
  loading = false,
  onToggleStatus,
}) => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  // Column sort — newest first by default.
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  // Filter by search query (name / email / phone / job title).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return responses.filter((r) => {
      const name = (r.full_name || "").toLowerCase();
      const email = (r.email || "").toLowerCase();
      const phone = (r.phone_number || "").toLowerCase();
      const job = (r.job_title || "").toLowerCase();
      return (
        !q ||
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        job.includes(q)
      );
    });
  }, [responses, query]);

  // Value used for comparing two rows on the active sort column.
  const sortValue = (r, key) => {
    switch (key) {
      case "name":
        return (r.full_name || "").toLowerCase();
      case "email":
        return (r.email || "").toLowerCase();
      case "phone":
        return (r.phone_number || "").toLowerCase();
      case "job":
        return (r.job_title || "").toLowerCase();
      case "date":
        return new Date(r.created_at || 0).getTime();
      case "viewed":
        return isActiveValue(r) ? 1 : 0;
      default:
        return "";
    }
  };

  // Sort the filtered rows by the active column + direction.
  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Click a header: flip direction if it's the active column, else sort it ascending.
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
    setPage(1);
  };

  const sortIcon = (key) => {
    if (sort.key !== key)
      return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sort.dir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const thClass =
    "text-gray-500 font-semibold uppercase text-[11px] tracking-wider";

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={handleSearch}
            placeholder="Search by name, email, phone or job…"
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition"
          />
        </div>

        <span className="ml-auto shrink-0 text-sm text-gray-500">
          {filtered.length} {filtered.length === 1 ? "response" : "responses"}
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b border-gray-200">
            <TableHead className={`${thClass} pl-6`}>
              <button
                type="button"
                onClick={() => toggleSort("name")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Name
                {sortIcon("name")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("email")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Email
                {sortIcon("email")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("phone")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Phone
                {sortIcon("phone")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("job")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Applied For
                {sortIcon("job")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("date")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Date
                {sortIcon("date")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("viewed")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Viewed
                {sortIcon("viewed")}
              </button>
            </TableHead>
            <TableHead className={`${thClass} text-right pr-6`}>CV</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-gray-500 py-12">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading responses…
                </span>
              </TableCell>
            </TableRow>
          ) : pageItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-12">
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Inbox className="h-8 w-8" />
                  <span className="text-sm">
                    {query
                      ? "No responses match your search."
                      : "No job responses yet."}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            pageItems.map((res) => {
              const active = isActiveValue(res);
              const deleted = isDeleted(res);

              return (
                <TableRow
                  key={res.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                >
                  {/* Name */}
                  <TableCell className="font-medium text-gray-900 pl-6 py-4">
                    {res.full_name || "—"}
                  </TableCell>

                  {/* Email */}
                  <TableCell className="py-4">
                    {res.email ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {res.email}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Phone */}
                  <TableCell className="py-4">
                    {res.phone_number ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        {res.phone_number}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Applied For — job title */}
                  <TableCell className="py-4">
                    {res.job_title ? (
                      <span className="inline-flex items-center rounded-full bg-[#b32126]/10 px-2.5 py-0.5 text-xs font-medium text-[#b32126]">
                        {res.job_title}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell className="text-gray-600 py-4 whitespace-nowrap">
                    {formatDate(res.created_at)}
                  </TableCell>

                  {/* Status toggle (is_active), or Deleted badge for trashed */}
                  <TableCell className="py-4">
                    {deleted ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600">
                        <Trash2 className="h-3 w-3" />
                        Deleted
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onToggleStatus?.(res)}
                        title={active ? "Active" : "Inactive"}
                        className={`relative h-6 w-11 rounded-full transition-colors ${
                          active ? "bg-green-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            active ? "translate-x-5" : ""
                          }`}
                        />
                      </button>
                    )}
                  </TableCell>

                  {/* CV download */}
                  <TableCell className="py-4 pr-6">
                    <div className="flex justify-end">
                      {res.cv ? (
                        <a
                          href={cvUrl(res.cv)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-[#b32126] hover:text-[#b32126] transition-colors"
                        >
                          <FileDown className="h-4 w-4" />
                          CV
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-medium transition-colors ${
                  p === currentPage
                    ? "bg-[#b32126] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobResponsesTable;
